<template>
  <PacksButtonGroupSkeleton v-if="loading" />
  <div
    v-else-if="varietyPacks?.length"
    class="flex flex-col items-center w-full max-w-3xl mx-auto"
    data-test="variety-packs-button-group"
  >
    <div class="text-sm text-gray-500 text-center">
      {{ $t('bundler.fill_your_box_with_our_best_selling_bundles') }}
    </div>
    <div class="flex w-full gap-1 py-2">
      <button
        v-for="(pack, index) in varietyPacks.slice(0, 7)"
        :key="pack.title"
        :aria-label="`Fill box with ${pack.title}`"
        class="flex-1 font-semibold p-1 rounded-full text-purple-500 border-2 border-purple-500 outline-offset-4 hover:text-white hover:bg-purple-500 whitespace-nowrap"
        data-gtm-action="variety pack button click"
        :data-gtm-label="pack.title"
        :class="index < 3 ? 'block' : 'hidden md:block'"
        @click="select(pack)"
      >
        {{ pack.title }}
      </button>
    </div>
    <div class="text-sm text-gray-500 text-center">
      {{ $t('bundler.or_personalize_your_flavors') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { getUrlParam, setUrlParams } from '@/utils'
import { useToastStore } from '@/plugins/toast'
import { useBundleStore, useThemeStore, useVarietyPacksStore } from '@/stores'
import { showBundleSummary } from '@/utils/bundle'
import { useAmplitude } from '@/services/amplitude'
import PacksButtonGroupSkeleton from './variety-packs-button-group-skeleton.vue'
import VarietyPackConfirmationModal from '@/components/variety-packs/variety-pack-confirmation-modal.vue'
import type { GraphqlVarietyPack } from '@/types/shopify'

const { alert } = useToastStore()
const { modal } = useThemeStore()
const store = useBundleStore()
const packsStore = useVarietyPacksStore()
const { varietyPacks, loading } = storeToRefs(packsStore)

const select = (pack: GraphqlVarietyPack, confirmation = 'alert') => {
  if (store.selectedSize < 16) {
    store.selectedSize = 16
  }
  store.setSelectionFromContents(packsStore.contents(pack, store.selectedSize))
  if (confirmation === 'modal') {
    modal.open(VarietyPackConfirmationModal, {
      pack
    })
  } else {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      alert(`We've filled your box with ${pack.title}.`)
    }
    showBundleSummary()
  }
  const { trackBundle } = useAmplitude()
  trackBundle(null, pack.title)
}

onMounted(async () => {
  try {
    await packsStore.useVarietyPacks()
    const handle = getUrlParam('curated')
    const confirmation = getUrlParam('confirmation') || 'alert'
    const pack = varietyPacks.value?.find(i => i.handle === handle)
    if (pack) {
      select(pack, confirmation)
    }
    setUrlParams({ curated: undefined, confirmation: undefined })
  } catch {
    // nothing
  }
})
</script>
